import { AvailableLocale } from '@models/Lang'
import { Program } from '@models/membership/Program'
import { SBAPI } from '@supplement-bacon/react-toolkit/utils'
import { TableParams } from '@supplement-bacon/sb-table-filters'
import { MEMBERSHIP_PROGRAMS_URL } from '@utils/urls'

const fetchPrograms = (params: TableParams) =>
  SBAPI.FetchPaginated<Program[]>(MEMBERSHIP_PROGRAMS_URL, params)

const fetchProgramBySlug = (slug: string, params?: TableParams) =>
  SBAPI.Fetch<Program>(`${MEMBERSHIP_PROGRAMS_URL}/${slug}`, params)

const createProgram = (program: Program) =>
  SBAPI.Post(MEMBERSHIP_PROGRAMS_URL, program)

const updateProgram = (program: Program) =>
  SBAPI.Put(`${MEMBERSHIP_PROGRAMS_URL}/${program.slug}`, program)

const updateProgramLogo = (program: Program, file: File) => {
  const data = new FormData()
  data.append('file', file)
  return SBAPI.Post(`${MEMBERSHIP_PROGRAMS_URL}/${program.slug}/logo`, data)
}

/**
 * Replace the perks booklet of one or more locales. Locales left out keep the
 * booklet they already have.
 */
const updateProgramPerksPdf = (
  program: Program,
  files: Partial<Record<AvailableLocale, File>>
) => {
  const data = new FormData()
  Object.entries(files).forEach(([locale, file]) => {
    if (file) {
      data.append(`${locale}[file]`, file)
    }
  })
  return SBAPI.Post(
    `${MEMBERSHIP_PROGRAMS_URL}/${program.slug}/perks-pdf`,
    data
  )
}

export type WalletAssetType = 'logo' | 'icon' | 'strip' | 'hero'

/**
 * Replace the master upload for one of the wallet pass images (logo, icon,
 * strip, hero). Single shared endpoint, `type` picks which asset — see
 * ProgramController::updateWalletAsset.
 */
const updateProgramWalletAsset = (
  program: Program,
  assetType: WalletAssetType,
  file: File
) => {
  const data = new FormData()
  data.append('type', assetType)
  data.append('file', file)
  return SBAPI.Post(
    `${MEMBERSHIP_PROGRAMS_URL}/${program.slug}/wallet-asset`,
    data
  )
}

const deleteProgram = (program: Program) =>
  SBAPI.Delete(`${MEMBERSHIP_PROGRAMS_URL}/${program.slug}`)

const fetchVideoDirectUploadUrl = (slug: string) =>
  SBAPI.Fetch<{ passthrough: string; url?: string }>(
    `${MEMBERSHIP_PROGRAMS_URL}/${slug}/video/direct-upload`
  )

const postVideoPassthroughUrl = (slug: string, videoPassthrough: string) =>
  SBAPI.Post(`${MEMBERSHIP_PROGRAMS_URL}/${slug}/video/passthrough`, {
    videoPassthrough,
  })

export {
  createProgram,
  deleteProgram,
  fetchProgramBySlug,
  fetchPrograms,
  fetchVideoDirectUploadUrl,
  postVideoPassthroughUrl,
  updateProgram,
  updateProgramLogo,
  updateProgramPerksPdf,
  updateProgramWalletAsset,
}
